import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { useSubmissionQueries } from "./useSubmissionQueries";
import { useStoreSelectQueries } from "../../../shared";
import { AppContext, AuthContext } from "../../../store";
import { useDebounce } from "use-debounce";

export const useSubmissionList = () => {
  const {
    globalData,
    tableData,
    setTableState,
    setSearchState,
    setSorting,
    setPagination,
    clearAllFilters,
  } = useContext(AppContext);
  const { auth } = useContext(AuthContext);

  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("/store-admin");
  const moduleName = "submissions" + (isStoreAdmin ? "Store" : "Admin");

  const tableState = tableData[moduleName];
  const [debouncedSearch] = useDebounce(tableState.search, 1000);

  const { listQuery: storeLists } = useStoreSelectQueries({
    storeList: !isStoreAdmin,
  });
  const stockTakeStatuses = globalData?.stocktake_statuses || [];

  // Build params for API
  const getListParams = () => {
    const params = {};
    params.page = tableState.pagination.pageIndex;
    params.per_page = tableState.pagination.pageSize;
    if (debouncedSearch) params.search = debouncedSearch;
    if (tableState.sorting.length > 0) {
      params.sort = tableState.sorting.reduce(
        (obj, item) =>
          Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
        {},
      );
    }
    if (tableState.data.storeFilter.length > 0) {
      params.store_id = tableState.data.storeFilter.map((s) => s.id);
    }
    if (isStoreAdmin) params.store_id = auth?.data?.selectedStoreId;

    return params;
  };

  const { submissionListQuery } = useSubmissionQueries({
    listParams: getListParams(),
  });

  const handleSearchChange = (value) => {
    setSearchState({ tableName: moduleName, key: "search", value });
    setPagination({
      tableName: moduleName,
      callback: (prev) => ({ ...prev, pageIndex: 1 }),
    });
  };

  const handleStoreFilter = (event, newValue) => {
    setTableState({
      tableName: moduleName,
      key: "storeFilter",
      value: newValue,
    });
    setPagination({
      tableName: moduleName,
      callback: (prev) => ({ ...prev, pageIndex: 1 }),
    });
  };

  const handleClearFilter = () => {
    clearAllFilters({ tableName: moduleName });
  };

  const handlePagination = (callback) => {
    setPagination({ tableName: moduleName, callback });
  };

  const handleSorting = (callback) => {
    setSorting({ tableName: moduleName, callback });
  };

  return {
    submissionListQuery,
    moduleName,
    tableState,
    search: tableState.search,
    setSearch: handleSearchChange,
    storeFilter: tableState.data.storeFilter,
    setStoreFilter: handleStoreFilter,
    handleClearFilter,
    pagination: tableState.pagination,
    setPagination: handlePagination,
    sorting: tableState.sorting,
    setSorting: handleSorting,
    isStoreAdmin,
    disableClearButton: tableState.data.storeFilter.length === 0,
    stockTakeStatuses,
    storeLists: storeLists?.data || [],
  };
};
